import React, { useContext, useEffect, useRef, useState, useCallback } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import { toast } from "react-toastify";

import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import useWebRTCCall from "../../hooks/useWebRTCCall";
import { markCallActive, clearCallActive } from "../callState";

const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

// mode: "start" (atendente inicia, envia link) | "answer" (atendente aceita chamada do cliente)
const NativeCall = ({ open, onClose, ticket, mode = "start" }) => {
  const { socket } = useContext(AuthContext);
  const [turn, setTurn] = useState(null);
  const [callId, setCallId] = useState(null);
  const [link, setLink] = useState("");
  const startedRef = useRef(false);

  const getSocket = useCallback(() => socket, [socket]);

  const { state, muted, seconds, startCall, hangup, toggleMute, remoteAudioRef } = useWebRTCCall({
    getSocket,
    ticketId: ticket?.id,
    turn,
    role: "caller",
  });

  useEffect(() => {
    if (!open || startedRef.current) return;
    startedRef.current = true;
    markCallActive(ticket.id);
    (async () => {
      try {
        const endpoint = mode === "answer" ? `/call/${ticket.id}/answer` : `/call/${ticket.id}/start`;
        const { data } = await api.post(endpoint);
        setTurn(data.turn);
        setCallId(data.callId || null);
        setLink(data.link || "");
        if (socket) socket.emit("call:join", { ticketId: ticket.id });
      } catch (err) {
        toast.error("Não foi possível iniciar a chamada.");
      }
    })();
  }, [open, ticket, socket, mode]);

  useEffect(() => {
    if (open && turn && state === "idle") startCall();
  }, [open, turn, state, startCall]);

  const finish = useCallback(async () => {
    const status = state === "in-call" ? "completed" : "missed";
    hangup();
    if (callId) {
      try { await api.post(`/call/${callId}/end`, { status, duration: seconds }); } catch (e) {}
    }
    clearCallActive(ticket?.id);
    startedRef.current = false;
    setTurn(null);
    setCallId(null);
    onClose();
  }, [state, hangup, callId, seconds, onClose, ticket]);

  const statusText = {
    idle: "Preparando…",
    calling: mode === "answer" ? "Conectando com o cliente…" : "Aguardando o cliente atender…",
    connecting: "Conectando…",
    "in-call": "Em chamada",
    ended: "Encerrada",
    failed: "Falha na chamada",
  }[state] || "";

  return (
    <Dialog open={open} onClose={finish} maxWidth="xs" fullWidth>
      <DialogTitle>Ligação (nativa) — {ticket?.contact?.name || "Contato"}</DialogTitle>
      <DialogContent>
        <audio ref={remoteAudioRef} autoPlay />
        <p style={{ fontSize: 15, margin: "4px 0 12px" }}>
          {statusText}{state === "in-call" ? ` · ${fmt(seconds)}` : ""}
        </p>
        {mode === "start" && state === "calling" && (
          <>
            <p style={{ fontSize: 13, color: "#777", marginTop: 0 }}>
              O link foi enviado no WhatsApp. Se preferir, copie e envie manualmente:
            </p>
            <TextField
              fullWidth
              size="small"
              value={link}
              InputProps={{ readOnly: true }}
              onFocus={(e) => e.target.select()}
            />
            <Button
              size="small"
              color="primary"
              style={{ marginTop: 8 }}
              onClick={() => { navigator.clipboard.writeText(link); toast.success("Link copiado!"); }}
            >
              Copiar link
            </Button>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {state === "in-call" && (
          <Button onClick={toggleMute}>{muted ? "Desmutar" : "Mutar"}</Button>
        )}
        <Button onClick={finish} variant="contained" color="secondary">Encerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default NativeCall;
